import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { QuickCustomizationService } from '../../../services/quick-customization.service';
import { ToastrService } from 'ngx-toastr';
import { CustomizationService } from '../../../services/customization.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { AddCustomizationModalComponent } from './modal/add-customization-modal/add-customization-modal.component';
import { RestaurantService } from '../../../services/restaurant.service';
import { isFnfLike, isPosManaged } from '../../../utils/pos-provider.util';

@Component({
  selector: 'app-customization',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './customization.component.html',
  styleUrl: './customization.component.css',
})
export class CustomizationComponent {
  ngxSpinnerService = inject(NgxSpinnerService);
  customizationService = inject(CustomizationService);
  private quickCustomization = inject(QuickCustomizationService);
  restaurantService = inject(RestaurantService);

  customizations: any = []; // Stores types and their associated categories
  unFilteredCustomizations: any = [];
  activeAccordionIndex: number | null = null; // Tracks the currently expanded accordion index
  displayLoading: boolean = false;
  restaurantPosProvider: string = 'fnf';

  /** Catalogue synced from Square / Clover / Swirepay, so editing is off. */
  get isPosManagedProvider(): boolean {
    return isPosManaged(this.restaurantPosProvider);
  }
  pageSize: number = 10;
  currentPage: number = 1;

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.customizations.length / this.pageSize));
  }

  get pagedCustomizations(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.customizations.slice(start, start + this.pageSize);
  }

  get footerText(): string {
    const groupWord = this.customizations.length === 1 ? 'group' : 'groups';
    let text = `Showing ${this.customizations.length} customization ${groupWord}`;
    if (this.activeAccordionIndex !== null) {
      const expanded = this.pagedCustomizations[this.activeAccordionIndex];
      if (expanded) {
        const activeCount = (expanded.types || []).filter((t: any) => t.in_stock !== false).length;
        text += ` (${activeCount} active option${activeCount === 1 ? '' : 's'} under ${expanded.name})`;
      }
    }
    return text;
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.activeAccordionIndex = null;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.activeAccordionIndex = null;
    }
  }

  selectionLabel(customization: any): string {
    const min = customization.min_selections ?? 0;
    const max = customization.max_selections ?? (customization.is_multi_select ? '∞' : 1);
    return `${customization.is_multi_select ? 'Multi' : 'Single'} · ${min}–${max}`;
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private toastr: ToastrService,
  ) {
    this.restaurantService.restaurantPosProvider$.subscribe((posProvider: any) => {
      this.restaurantPosProvider = posProvider;
    });
  }

  ngOnInit() {
    this.getCustomizations();
  }

  toggleAccordion(index: number) {
    if (this.activeAccordionIndex === index) {
      this.activeAccordionIndex = null; // Collapse if the same index is clicked
    } else {
      this.activeAccordionIndex = index; // Expand the clicked accordion
    }
  }

  getCustomizations() {
    this.displayLoading = true;
    this.customizationService.getCustomizations().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          // Sort customizations alphabetically by name
          const sortedData = [...response.data].sort((a: any, b: any) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
          );
          this.customizations = sortedData;
          this.customizations.forEach((c: any) => {
            if (
              c.external_references &&
              c.external_references.clover !== null &&
              c.external_references.clover !== undefined
            ) {
              c.source = 'clover';
              c.source_logo = 'assets/clover.png';
            } else if (
              c.external_references &&
              c.external_references.uber !== null &&
              c.external_references.uber !== undefined
            ) {
              c.source = 'uber';
              c.source_logo = 'assets/uber_eats.png';
            } else if (
              c.external_references &&
              c.external_references.square !== null &&
              c.external_references.square !== undefined
            ) {
              c.source = 'square';
              c.source_logo = 'assets/square.webp';
            } else if (
              c.external_references &&
              c.external_references.swirepay !== null &&
              c.external_references.swirepay !== undefined
            ) {
              c.source = 'swirepay';
              c.source_logo = 'assets/swirepay.png';
            } else {
              c.source = 'fnf';
              c.source_logo = 'assets/forknfind.png';
            }
          });
          this.unFilteredCustomizations = sortedData;
        }
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  addNewCustomization(customization?: any) {
    if (isPosManaged(this.restaurantPosProvider) && !customization) {
      return;
    }
    const dialogRef = this.dialog.open(AddCustomizationModalComponent, {
      data: {
        customization: customization ? customization : null,
        allCustomization: this.customizations,
      },
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'czm-fullscreen-dialog',
      // Material's scale-in would fight the sheet's slide-up.
      enterAnimationDuration: '0ms',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined && result != '') {
        if (customization) {
          let options: any = [];
          result.options.forEach((f: any) => {
            options.push({
              value: f.name,
              description: f.description ?? '',
              price: f.price ? f.price : 0,
              is_default: !!f.isChecked,
              in_stock: f.inStock !== false,
              type_id: f.type_id,
            });
          });

          let payload: any = {
            name: result.name,
            description: result.description ?? '',
            types: options,
            remarks: result.remarks,
            default: result.defaultValue,
            is_multi_select: result.inputType == 'Multi Selection' ? true : false,
            // 0 clears a stored constraint (normalized to "no constraint").
            min_selections: result.minSelections ?? 0,
            max_selections: result.maxSelections ?? 0,
            is_price_different: result.priceDiffersForEachOption,
            is_customer_visible: result.isCustomerVisible,
          };

          // POS-managed (clover/square) content is read-only and synced from the
          // POS: send only the FnF-owned visibility flag. Echo the stored default
          // because the backend PATCH overwrites it with whatever is sent.
          if (isPosManaged(this.restaurantPosProvider)) {
            payload = {
              is_customer_visible: result.isCustomerVisible,
              default: customization.default,
            };
          }

          this.editCustomization(payload, customization.id);
        } else {
          this.saveCustomization(result);
        }
      }
    });
  }

  saveCustomization(customization: any) {
    this.quickCustomization.save(customization).subscribe((saved) => {
      if (saved) {
        this.getCustomizations();
      }
    });
  }

  editCustomization(customization: any, id: any) {
    this.ngxSpinnerService.show();
    this.customizationService.editCustomization(customization, id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Customization updated successfully', 'Success');
          this.getCustomizations();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  deleteCustomizationConfirm(customization: any) {
    if (customization.is_deletable && isFnfLike(this.restaurantPosProvider)) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message:
            'This will permanently delete the ' +
            customization.name +
            ' and you can’t undo this later.',
          buttonText: {
            ok: 'Yes, Delete',
            cancel: 'No, Cancel',
          },
        },
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteCustomization(customization);
        }
      });
    }
  }

  deleteCustomization(customization: any) {
    this.ngxSpinnerService.show();
    this.customizationService.deleteCustomization(customization.id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.toastr.success('Customization deleted successfully', 'Success');
        this.ngxSpinnerService.hide();
        this.getCustomizations();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  checkDefault(customization: any, value: string) {
    if (
      customization.is_multi_select &&
      customization.default &&
      customization.default.length > 0
    ) {
      return customization.default.includes(value);
    }
  }

  searchCustomization(event: any) {
    this.customizations = this.unFilteredCustomizations.filter((f: any) =>
      f.name.toLowerCase().includes(event.target.value.toLowerCase()),
    );
    this.currentPage = 1;
    this.activeAccordionIndex = null;
  }
}
